// @flow

import React, { Component } from 'react';
import { connect } from 'react-redux';
import ClaimedWebsiteMark from 'app/common/react/components/profile/ProfileHeader/ClaimedWebsiteMark';
import ContentHeader from 'app/common/react/ui/ContentHeader';
import cx from 'classnames';
import i18n from 'app/common/lib/i18n';
import NavigateLink from 'app/common/react/ui/NavigateLink';
import PrettyNumber from 'app/common/react/i18n/PrettyNumber';
import PropTypes from 'prop-types';
import type { ProfileUser } from 'app/packages/pinterest-flow-types/user';
import type { Viewer } from 'app/packages/pinterest-flow-types/viewer';
import urlUtil from 'app/common/lib/urlUtil';
import UserFollowButton from 'app/common/react/components/FeedsAndFollowing/FollowButton/UserFollowButton';
import withViewer from 'app/common/lib/react/withViewer';
import { compose } from 'redux';
import { HEADER_DEFAULT_HEIGHT } from 'app/common/react/components/Header/HeaderConstants';
import { interpolateNamedTemplate } from 'app/common/lib/react/interpolate';
import { prettifyUrl } from 'app/packages/url/formatUrl';
import withUnauthSeoExperiments, {
  type UnauthSeoExperiments,
} from 'app/common/lib/react/withUnauthSeoExperiments';
import {
  Avatar,
  Box,
  Button,
  Column,
  Container,
  Flyout,
  Heading,
  IconButton,
  Text,
  Touchable,
} from 'gestalt';

type ReduxProps = {|
  shouldShowLatestNag?: boolean,
|};

type Props = {
  onClickReportBug: () => void,
  onClickSendProfile: () => void,
  onCreatePincodeClick: () => void,
  onScroll: () => void,
  onToggleBlock: () => void,
  onToggleFollow: () => void,
  openLoginModal: *,
  scrollStep: 1 | 2 | 3,
  seoUnauthExperiments: UnauthSeoExperiments,
  showFixedHeaderName: ?boolean,
  user: ProfileUser,
  viewer: Viewer,
  ...ReduxProps,
};

type Context = {
  country: string,
};

type State = {
  showProfileOptions: boolean,
  top: number,
};

function formatDisplayUrl(url: string) {
  const prettyUrl = prettifyUrl(url);
  return urlUtil.punycode_decode(prettyUrl);
}

function isOwnProfile({ user, viewer }: { user: ProfileUser, viewer: Viewer }) {
  return viewer.isAuth && user.username === viewer.username;
}

export class ProfileCreatorHeader extends Component<Props, State> {
  static contextTypes = {
    country: PropTypes.string.isRequired,
  };

  //TODO: Integrate nag events with new profile
  state = {
    showProfileOptions: false,
    top: HEADER_DEFAULT_HEIGHT,
  };

  componentDidMount() {
    window.addEventListener('scroll', this.onScroll);

    this.handleNagHeader();
  }

  componentWillUnmount() {
    window.removeEventListener('scroll', this.onScroll);
  }

  onScroll = () => this.props.onScroll();

  setContainerRef = (ref: ?HTMLDivElement) => (this.container = ref);

  handleNagHeader = () => {
    const { shouldShowLatestNag } = this.props;

    if (shouldShowLatestNag) {
      this.setState({
        top: HEADER_DEFAULT_HEIGHT,
      });
    }
  };

  profileOptionsContainer: ?HTMLElement;

  context: Context;

  container: ?HTMLElement;

  prevScrollY: number = 0;

  pincodeEnabled = (): boolean => {
    const { user, viewer } = this.props;
    return isOwnProfile({ user, viewer }) || (viewer.isAuth && viewer.isEmployee);
  };

  //TODO: Figure out where to show this on creator profile
  shouldShowImpression = (): boolean => {
    const {
      user: { impressum_url: impressumUrl },
    } = this.props;
    const { country } = this.context;

    // For legal reasons we need to sometimes show the impressum URL in certain countries
    // http://www.allthingsgerman.net/blog/blogging/blogging-in-germany-the-impressum/#.WdV56ROPJE4
    return !!(impressumUrl && ['DE', 'AT', 'CH'].includes(country.toUpperCase()));
  };

  hideProfileOptions = () => this.setState({ showProfileOptions: false });

  showProfileOptions = () => this.setState({ showProfileOptions: true });

  renderUnblockButton() {
    const { onToggleBlock } = this.props;
    return (
      <Button color="gray" inline onClick={onToggleBlock} size="md" text={i18n._('Unblock')} />
    );
  }

  renderFollowButton() {
    const { onToggleFollow, scrollStep, user, viewer } = this.props;
    const {
      explicitly_followed_by_me,
      username,
      full_name: fullName,
      id: userId,
      image_large_url: imageUrl,
    } = user;
    const profileUrl = `/${username}/overview/`;

    return (
      <ContentHeader
        className={cx('CreatorFollowButton', { scroll: scrollStep }, `step${scrollStep}`)}
        position={!!scrollStep ? 'fixed' : undefined}
      >
        <Box display="flex" alignItems="center">
          <Box>
            <UserFollowButton
              fullName={fullName}
              hideFollowingText
              id={userId}
              imageUrl={imageUrl || ''}
              inline
              isOwnProfile={isOwnProfile({ user, viewer })}
              onUserFollow={onToggleFollow}
              profileUrl={profileUrl}
              showFollowerCount
              userFollowed={explicitly_followed_by_me}
            />
          </Box>
        </Box>
      </ContentHeader>
    );
  }

  renderCreatorInformation = () => {
    const {
      onClickSendProfile,
      onCreatePincodeClick,
      onToggleBlock,
      scrollStep,
      user,
      viewer,
    } = this.props;

    const formattedMonthlyViewers =
      user.profile_reach >= 10000000 ? (
        i18n._('10m+', 'text for number of more than 10 millions')
      ) : (
        <PrettyNumber
          value={user.profile_reach || 0}
          shortform
          shortformMaximumFractionDigits={1}
        />
      );

    // Both have marginRight=1 to support i18n in case they get swapped
    const monthlyViewers = interpolateNamedTemplate(i18n._('{{ count }} {{ label }}'), {
      count: (
        <Box marginRight={1} key="count">
          <Text bold size="lg">
            {formattedMonthlyViewers}
          </Text>
        </Box>
      ),
      label: (
        <Box marginRight={1} key="label">
          <Text size="lg">{i18n._('monthly viewers', 'Monthly Viewers label')}</Text>
        </Box>
      ),
    });

    const isViewerProfile = isOwnProfile({ user, viewer });

    const { blocked_by_me } = user;
    const { showProfileOptions } = this.state;
    const showVerifiedIcon = user.verified_identity && user.verified_identity.verified;
    const avatar = (
      <ContentHeader
        className={cx('AvatarScroller', { scroll: scrollStep }, `step${scrollStep}`)}
        position={!!scrollStep ? 'fixed' : undefined}
      >
        <div className="ShadowedAvatarWrapper">
          <Avatar name={user.full_name} src={user.image_xlarge_url} verified={showVerifiedIcon} />
        </div>
      </ContentHeader>
    );

    const followButton = blocked_by_me ? this.renderUnblockButton() : this.renderFollowButton();

    const optionsFlyout =
      showProfileOptions && !scrollStep ? (
        <Flyout
          anchor={this.profileOptionsContainer}
          idealDirection="down"
          onDismiss={this.hideProfileOptions}
          size="sm"
        >
          <Column xs={12}>
            <Button
              accessibilityLabel={i18n._('Share', 'Share profile')}
              color="white"
              text={i18n._('Share', 'Share profile')}
              onClick={onClickSendProfile}
            />
            {!blocked_by_me && (
              <Button
                accessibilityLabel={i18n._('Block', 'Button text to enter the block user flow')}
                color="white"
                text={i18n._('Block', 'Button text to enter the block user flow')}
                onClick={onToggleBlock}
              />
            )}
          </Column>
        </Flyout>
      ) : null;

    const profileOptionsBox =
      viewer && viewer.isAuth ? (
        <Box className="profile-options-box" marginRight={4}>
          {!isViewerProfile && (
            <div
              ref={(ref) => {
                this.profileOptionsContainer = ref;
              }}
            >
              <IconButton
                accessibilityLabel={i18n._(
                  'Profile options',
                  'Accessible label for button to show share or report flyout',
                )}
                bgColor="lightGray"
                icon="ellipsis"
                onClick={this.showProfileOptions}
                size="xs"
              />
            </div>
          )}
          {optionsFlyout}
        </Box>
      ) : null;

    return (
      <Box>
        <Box display="flex" alignItems="start">
          <Column span={3}>
            <Box
              paddingX={3}
              marginLeft={-3}
              dangerouslySetInlineStyle={{ __style: { marginTop: -50 } }}
            >
              {this.pincodeEnabled() ? (
                <Touchable onTouch={onCreatePincodeClick} shape="circle" fullWidth={false}>
                  {avatar}
                </Touchable>
              ) : (
                <Box>{avatar}</Box>
              )}
            </Box>
          </Column>
          <Box alignSelf="start" column={9}>
            <Box
              alignItems="start"
              dangerouslySetInlineStyle={{ __style: { marginTop: 20 } }}
              display="flex"
              justifyContent="between"
              wrap
            >
              <Box column={7} paddingX={2} flex="grow" marginBottom={2}>
                <Heading mdSize="sm" size="xs" overflow="normal">
                  {user.full_name}
                </Heading>
                {user.profile_reach !== -1 && (
                  <Box display="flex" marginTop={2}>
                    {monthlyViewers}
                  </Box>
                )}
              </Box>
              <Box alignItems="center" display="flex" flex="shrink">
                {profileOptionsBox}
                <Box column={12} flex="shrink">
                  {!isViewerProfile && followButton}
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
        <Box display="flex" justifyContent="between" marginTop={3} wrap>
          <Column span={5} mdSpan={6}>
            <Box marginRight={4}>
              {user.location && (
                <Box>
                  <Text bold color="gray" size="lg">
                    {user.location}
                  </Text>
                </Box>
              )}
              {user.website_url && (
                <Box marginTop={2} display="flex" alignItems="center">
                  {// $FlowFixMe: new errors found from flow 0.96 upgrade
                  user.domain_verified && <ClaimedWebsiteMark />}
                  <NavigateLink to={user.website_url} style={{ width: '100%' }}>
                    <Text bold truncate size="lg">
                      {formatDisplayUrl(user.website_url)}
                    </Text>
                  </NavigateLink>
                </Box>
              )}
              {this.shouldShowImpression() && (
                <Box marginTop={2}>
                  <NavigateLink to={user.impressum_url}>
                    <Text bold color="gray" truncate size="lg">
                      {i18n._('Impressum', 'Impressum link')}
                    </Text>
                  </NavigateLink>
                </Box>
              )}
            </Box>
          </Column>
          <Column span={12} mdSpan={6}>
            <Box>
              <Text size="lg">{user.about}</Text>
            </Box>
          </Column>
        </Box>
      </Box>
    );
  };

  render() {
    return (
      <div className="CreatorHeaderContainer" ref={this.setContainerRef}>
        <Container>{this.renderCreatorInformation()}</Container>
      </div>
    );
  }
}

const mapState = ({ shouldShowLatestNag }) => ({
  shouldShowLatestNag,
});

export default compose(
  // $FlowFixMe: new errors found from flow 0.96 upgrade
  connect(mapState),
  withViewer,
  withUnauthSeoExperiments,
)(ProfileCreatorHeader);
